import {
  connectOrganisationGoogleDrive,
  connectPersonalGoogleDrive,
  disconnectOrganisationGoogleDrive,
  disconnectPersonalGoogleDrive,
  getOrganisationGoogleDriveAuthUrl,
  getOrganisationGoogleDriveStatus,
  getPersonalGoogleDriveAuthUrl,
  getPersonalGoogleDriveStatus,
  type GoogleDriveIntegrationStatus,
} from '@/api';
import { extractErrorDetail } from '@/utils/errorHandler';

/**
 * Which Drive a call is about. The backend exposes the caller's own Drive
 * (`user`, works without an active organisation) and the organisation's Drive
 * (`organisation`, admin-only to connect) as separate endpoint families, so
 * each helper here dispatches on the scope instead of passing it as a query.
 */
export type GoogleDriveIntegrationScope = 'user' | 'organisation';

export async function requestGoogleDriveAuthorizationUrl(
  scope: GoogleDriveIntegrationScope = 'organisation'
): Promise<string> {
  const response =
    scope === 'user'
      ? await getPersonalGoogleDriveAuthUrl()
      : await getOrganisationGoogleDriveAuthUrl();

  if (response.error) {
    throw new Error(
      extractErrorDetail(response.error, 'Failed to start the Google Drive connection.')
    );
  }

  const directUrl = response.data.url;
  if (typeof directUrl === 'string' && directUrl.trim()) {
    return directUrl;
  }

  throw new Error('No authorization URL was returned by the backend.');
}

export async function completeGoogleDriveOAuth(
  code: string,
  scope: GoogleDriveIntegrationScope = 'organisation'
): Promise<GoogleDriveIntegrationStatus> {
  const response =
    scope === 'user'
      ? await connectPersonalGoogleDrive({ body: { code } })
      : await connectOrganisationGoogleDrive({ body: { code } });

  if (response.error) {
    throw new Error(
      extractErrorDetail(response.error, 'Failed to complete the Google Drive connection.')
    );
  }

  return response.data;
}

export async function fetchGoogleDriveIntegrationStatus(
  scope: GoogleDriveIntegrationScope = 'organisation'
): Promise<GoogleDriveIntegrationStatus> {
  const { data } =
    scope === 'user'
      ? await getPersonalGoogleDriveStatus({ throwOnError: true })
      : await getOrganisationGoogleDriveStatus({ throwOnError: true });
  return data;
}

export async function disconnectGoogleDriveIntegration(
  scope: GoogleDriveIntegrationScope = 'organisation'
): Promise<GoogleDriveIntegrationStatus> {
  const { data } =
    scope === 'user'
      ? await disconnectPersonalGoogleDrive({ throwOnError: true })
      : await disconnectOrganisationGoogleDrive({ throwOnError: true });
  return data;
}
